import * as z from 'zod'

type ContentfulEntrySchema<ContentType extends z.core.util.Literal, Fields extends z.ZodLazy> = z.ZodLazy<
  z.ZodObject<{
    sys: z.ZodObject<{
      id: z.ZodString
      updatedAt: z.ZodOptional<z.ZodString>
      createdAt: z.ZodOptional<z.ZodString>

      contentType: z.ZodReadonly<
        z.ZodObject<{
          sys: z.ZodReadonly<
            z.ZodObject<{
              id: z.ZodReadonly<z.ZodLiteral<ContentType>>
            }>
          >
        }>
      >
    }>

    fields: Fields
  }>
>

/**
 * A function to build a zod schema for a Contentful entry.
 *
 * @param contentType The Contentful content type ID
 * @param options Additional options to build the schema
 * @param options.fields A zod schema representing the fields of the entry
 */
export function buildEntrySchemaFor<ContentType extends z.core.util.Literal, Fields extends z.ZodLazy>(
  contentType: ContentType,
  options: {
    fields: Fields
  },
): ContentfulEntrySchema<ContentType, Fields> {
  return z.lazy(() =>
    z.object({
      sys: z.object({
        id: z.string(),
        updatedAt: z.string().optional(),
        createdAt: z.string().optional(),
        contentType: z
          .object({
            sys: z
              .object({
                id: z
                  .literal(contentType, {
                    error: ctx => `Expected content type ${contentType}, but got ${ctx.input} instead.`,
                  })
                  .readonly(),
              })
              .readonly(),
          })
          .readonly(),
      }),
      fields: options.fields,
    }),
  )
}
